/**
 * Per-game betting limits. Configure in each game's `configs/GameConfig.ts`
 * via `betLimits` on the object passed to `createCrashGameStore`.
 */

export interface GameBetLimits {
  /** Minimum stake per bet. */
  minBet: number;
  /** Maximum stake per bet. */
  maxBet: number;
  /** Maximum cashout multiplier allowed for a single bet. */
  maxPayoutMultiplier: number;
  /** Maximum win amount for a single bet (maxBet × maxPayoutMultiplier). */
  maxWinPerBet: number;
  /** Stake increment/decrement step (+ / − buttons). Used when `stakeValues` is not set. */
  stakeStep: number;
  /**
   * Allowed stake values for +/− buttons (discrete list). When set, increment/decrement
   * walk this array instead of using `stakeStep` arithmetic.
   */
  stakeValues?: readonly number[];
  /** Default stake when the bet box is first shown or reset. */
  defaultStake: number;
  /** Minimum auto-cashout multiplier. */
  minAutoCashout: number;
  /** Maximum auto-cashout multiplier. */
  maxAutoCashout: number;
  /** Default auto-cashout multiplier. */
  defaultAutoCashout: number;
}

export const DEFAULT_GAME_BET_LIMITS: GameBetLimits = {
  minBet: 0.1,
  maxBet: 100,
  maxPayoutMultiplier: 100,
  maxWinPerBet: 10_000,
  stakeStep: 0.1,
  defaultStake: 1.0,
  minAutoCashout: 1.01,
  maxAutoCashout: 100,
  defaultAutoCashout: 1.1,
};

/** Maximum win for one bet = bet amount × maximum payout multiplier. */
export function computeMaxWinPerBet(
  betAmount: number,
  maxPayoutMultiplier: number
): number {
  return betAmount * maxPayoutMultiplier;
}

export function resolveGameBetLimits(
  overrides?: Partial<GameBetLimits>
): GameBetLimits {
  const limits = { ...DEFAULT_GAME_BET_LIMITS, ...overrides };
  limits.maxWinPerBet = computeMaxWinPerBet(
    limits.maxBet,
    limits.maxPayoutMultiplier
  );
  return limits;
}

export function clampStake(stake: number, limits: GameBetLimits): number {
  return Math.min(limits.maxBet, Math.max(limits.minBet, stake));
}

/** Compare stakes at 2 decimal places to avoid floating-point drift. */
function toStakeCents(value: number): number {
  return Math.round(value * 100);
}

/** Next allowed stake strictly above `current`, or the list maximum. */
export function incrementStakeInList(
  current: number,
  values: readonly number[]
): number {
  const currentCents = toStakeCents(current);
  for (const value of values) {
    if (toStakeCents(value) > currentCents) {
      return value;
    }
  }
  return values[values.length - 1] ?? current;
}

/** Previous allowed stake strictly below `current`, or the list minimum. */
export function decrementStakeInList(
  current: number,
  values: readonly number[]
): number {
  if (!values.length) return current;
  const currentCents = toStakeCents(current);
  if (currentCents <= toStakeCents(values[0])) {
    return values[0];
  }
  let result = values[0];
  for (const value of values) {
    if (toStakeCents(value) >= currentCents) {
      break;
    }
    result = value;
  }
  return result;
}

/** Snap a typed stake to the nearest allowed list value (after min/max clamp). */
export function snapStakeToList(value: number, limits: GameBetLimits): number {
  const values = limits.stakeValues;
  if (!values?.length) {
    return clampStake(value, limits);
  }
  const clamped = clampStake(value, limits);
  return values.reduce((prev, curr) =>
    Math.abs(curr - clamped) < Math.abs(prev - clamped) ? curr : prev
  );
}

export function isStakeAtListMin(
  stake: number,
  values: readonly number[]
): boolean {
  if (!values.length) return false;
  return toStakeCents(stake) <= toStakeCents(values[0]);
}

export function isStakeAtListMax(
  stake: number,
  values: readonly number[]
): boolean {
  if (!values.length) return false;
  return toStakeCents(stake) >= toStakeCents(values[values.length - 1]);
}

export function clampAutoCashout(value: number, limits: GameBetLimits): number {
  return Math.min(
    limits.maxAutoCashout,
    Math.max(limits.minAutoCashout, value)
  );
}

/** Display formatting for limits modals (e.g. 10,000.00 / 10.000,00). */
export function formatGameLimit(value: number): string {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
